import { Capacitor } from '@capacitor/core';
import { Keyboard, type KeyboardInfo } from '@capacitor/keyboard';
import { env } from '@config/env';

/**
 * Native keyboard handling: shift the *entire* app interface up instead of
 * letting the WebView resize (which reflows/compacts full-height layouts and
 * forces scrolling). Pairs with `Keyboard.resize = 'none'` in the wrapper
 * capacitor.config — the WebView stays full-screen and the keyboard overlays
 * it, while this controller translates `#root` up just enough to keep the
 * focused field above the keyboard.
 *
 * The shift is focus-aware: it moves the UI by the minimum needed so the
 * active input's bottom (plus a small margin) clears the keyboard top, rather
 * than always jumping the full keyboard height. Recomputes on focus changes
 * while the keyboard is open (e.g. tabbing between fields).
 */

const FOCUS_MARGIN = 16; // breathing room between the input and the keyboard
let keyboardHeight = 0;

function shiftTarget(): HTMLElement | null {
  return document.getElementById('root');
}

function focusedField(): HTMLElement | null {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return null;
  const tag = el.tagName;
  const editable = el.isContentEditable;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || editable) return el;
  return null;
}

function applyShift(): void {
  const root = shiftTarget();
  if (!root) return;

  if (keyboardHeight <= 0) {
    root.style.setProperty('--keyboard-shift', '0px');
    return;
  }

  const field = focusedField();
  if (!field) {
    root.style.setProperty('--keyboard-shift', '0px');
    return;
  }

  // The rect is measured with the CURRENT transform already applied. Since we
  // set an *absolute* shift, we must undo the applied translateY first —
  // otherwise advancing to a lower field while a shift is active compounds and
  // under-shifts (the new field stays behind the keyboard). Read the live
  // matrix so the math holds even mid-transition.
  let appliedTy = 0;
  const transform = getComputedStyle(root).transform;
  if (transform && transform !== 'none') {
    try {
      appliedTy = new DOMMatrixReadOnly(transform).m42; // negative when shifted up
    } catch {
      appliedTy = 0;
    }
  }

  const rect = field.getBoundingClientRect();
  const naturalBottom = rect.bottom - appliedTy; // field position with no shift
  const keyboardTop = window.innerHeight - keyboardHeight;
  const overlap = naturalBottom + FOCUS_MARGIN - keyboardTop;

  // Only shift if the field would actually be covered; never shift more than
  // the keyboard height (no need) and never negative.
  const shift = Math.max(0, Math.min(overlap, keyboardHeight + FOCUS_MARGIN));
  root.style.setProperty('--keyboard-shift', `${Math.round(shift)}px`);
}

let started = false;

export function initKeyboardShift(): void {
  // Only for native builds whose wrapper uses `Keyboard.resize: 'none'`
  // (flagged via env.keyboardShift). With native/body resize the WebView
  // already shrinks, so adding a transform would double-move the layout.
  //
  // ANDROID IS EXCLUDED: `Keyboard.resize` is an iOS-only config — the Android
  // plugin reads only `resizeOnFullScreen`, so the WebView always resizes
  // (windowSoftInputMode defaults to adjustResize). Shifting on top of that
  // double-moves the layout and leaves a keyboard-sized blank band. Android
  // therefore relies on the native resize alone, as the Tskr wrapper does.
  if (started || !Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'android') return;
  if (!env.keyboardShift) return;
  started = true;

  // Arms the transform in globals.css. It stays off everywhere else, because a transform on
  // #root would make it the containing block for every `position: fixed` shell in the app.
  shiftTarget()?.classList.add('keyboard-shift');

  void Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
    keyboardHeight = info.keyboardHeight || 0;
    applyShift();
  });

  void Keyboard.addListener('keyboardWillHide', () => {
    keyboardHeight = 0;
    applyShift();
  });

  // Tabbing to another field while the keyboard stays open doesn't refire
  // keyboardWillShow — recompute on focus changes. The rAF lets layout settle
  // (e.g. a sheet finishing its open transition) before measuring.
  document.addEventListener(
    'focusin',
    () => {
      if (keyboardHeight > 0) requestAnimationFrame(applyShift);
    },
    { passive: true },
  );
}
